/**
 * The phone's copy of the event log: imported history plus everything recorded on this
 * phone. Every action is written here first, so nothing depends on signal.
 *
 * Storage is localStorage for the prototype (wrapped: private mode or a full disk must
 * never break recording; the in-memory log keeps working and the UI says so).
 * Sync goes through a `SyncTransport`; the prototype ships a demo transport that only
 * pretends to upload, labelled as such. The real one posts to the Worker API.
 */
import type { EventEnvelope } from "@hitl/core";
import type { EventDraft } from "./session";

export type SyncState = "saved" | "syncing" | "offline" | "storage-unavailable";

export interface SyncTransport {
  upload(events: readonly EventEnvelope[]): Promise<void>;
}

export interface EventLog {
  all(): readonly EventEnvelope[];
  append(drafts: readonly EventDraft[]): EventEnvelope[];
  pending(): number;
  syncState(): SyncState;
  subscribe(listener: () => void): () => void;
  /** Try to upload what's pending (called on reconnect and after each append). */
  sync(): Promise<void>;
  resetLocal(): void;
  /**
   * Replace this phone's records with these (restoring a backup). Records already in the seed are
   * left to the seed, so nothing is doubled. Everything restored counts as not yet uploaded: when
   * real sync exists, uploads are matched by record id, so sending one twice is harmless.
   * Returns the records it replaced, so the restore can be undone.
   */
  replaceLocal(events: readonly EventEnvelope[]): EventEnvelope[];
}

interface Persisted {
  readonly events: EventEnvelope[];
  readonly deviceSeq: number;
  readonly syncedCount: number;
}

function read(key: string): Persisted | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Persisted) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: Persisted): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function deviceIdFor(key: string): string {
  try {
    const existing = localStorage.getItem(`${key}:device`);
    if (existing) return existing;
    const created = `phone-${crypto.randomUUID().slice(0, 8)}`;
    localStorage.setItem(`${key}:device`, created);
    return created;
  } catch {
    return `phone-${crypto.randomUUID().slice(0, 8)}`;
  }
}

export function createEventLog(options: {
  storageKey: string;
  seedEvents: readonly EventEnvelope[];
  /** Who is using this phone right now; read at the moment each record is made. */
  recordedBy: string | (() => string);
  transport: SyncTransport;
  isOnline: () => boolean;
}): EventLog {
  const { storageKey, seedEvents, recordedBy, transport, isOnline } = options;
  const deviceId = deviceIdFor(storageKey);
  let state: Persisted = read(storageKey) ?? { events: [], deviceSeq: 0, syncedCount: 0 };
  let storageOk = true;
  // Stable snapshot for React's useSyncExternalStore: a new array only when the log changes.
  let snapshot: readonly EventEnvelope[] = [...seedEvents, ...state.events];
  let uploading = false;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());
  const persist = () => {
    storageOk = write(storageKey, state);
    snapshot = [...seedEvents, ...state.events];
  };

  const log: EventLog = {
    all: () => snapshot,
    pending: () => state.events.length - state.syncedCount,
    syncState() {
      if (!storageOk) return "storage-unavailable";
      if (uploading) return "syncing";
      if (log.pending() > 0) return isOnline() ? "syncing" : "offline";
      return "saved";
    },
    append(drafts) {
      const created = drafts.map((draft, i): EventEnvelope => ({
        id: crypto.randomUUID(),
        type: draft.type,
        schemaVersion: 1,
        occurredAt: new Date().toISOString(),
        recordedBy: typeof recordedBy === "function" ? recordedBy() : recordedBy,
        deviceId,
        deviceSeq: state.deviceSeq + i + 1,
        payload: draft.payload,
      }));
      state = { ...state, events: [...state.events, ...created], deviceSeq: state.deviceSeq + created.length };
      persist();
      emit();
      void log.sync();
      return created;
    },
    async sync() {
      if (uploading || log.pending() === 0 || !isOnline()) {
        emit();
        return;
      }
      uploading = true;
      emit();
      const batch = state.events.slice(state.syncedCount);
      try {
        await transport.upload(batch);
        state = { ...state, syncedCount: state.syncedCount + batch.length };
        persist();
      } catch {
        // Stays pending; retried on the next append or reconnect.
      } finally {
        uploading = false;
        emit();
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    resetLocal() {
      state = { events: [], deviceSeq: state.deviceSeq, syncedCount: 0 };
      persist();
      emit();
    },
    replaceLocal(events) {
      const replaced = state.events;
      const seeded = new Set(seedEvents.map((e) => e.id));
      state = { events: events.filter((e) => !seeded.has(e.id)), deviceSeq: state.deviceSeq, syncedCount: 0 };
      persist();
      emit();
      void log.sync();
      return replaced;
    },
  };
  return log;
}

/** DEMO ONLY: pretends to upload. Replace with the Worker API transport. */
export const demoTransport: SyncTransport = {
  upload: () => new Promise((resolve) => setTimeout(resolve, 700)),
};
